import {
  QUESTIONNAIRE_TEMPLATE_ID,
  QUESTIONNAIRE_TEMPLATE_VERSIONS,
  YOUNG_QUESTIONNAIRE_MAX_AGE,
  YOUNG_QUESTIONNAIRE_MIN_AGE,
  YOUNG_QUESTIONNAIRE_TEMPLATE_ID,
} from '../data/constants';
import { parseDobToIso } from './identity';
import type { QuestionnaireDraft } from './questionnaire-draft';

/** Which questionnaire the patient answers. */
export type QuestionnaireVariant = 'adult' | 'young';

/** Age range derived before the questionnaire starts; under-min is not supported. */
export type AgeRangeResolution = QuestionnaireVariant | 'under-min';

export interface QuestionnaireTemplateConfig {
  templateId: string;
  templateVersion: number;
}

const TEMPLATE_ID_BY_VARIANT: Record<QuestionnaireVariant, string> = {
  adult: QUESTIONNAIRE_TEMPLATE_ID,
  young: YOUNG_QUESTIONNAIRE_TEMPLATE_ID,
};

export const getQuestionnaireTemplateConfig = (
  variant: QuestionnaireVariant,
): QuestionnaireTemplateConfig => {
  const templateId = TEMPLATE_ID_BY_VARIANT[variant];
  return { templateId, templateVersion: QUESTIONNAIRE_TEMPLATE_VERSIONS[templateId] };
};

/** Whole years between the date of birth and `today`, in local time; null if unreadable. */
export const calculateAgeOn = (dateOfBirth: string, today: Date): number | null => {
  const iso = parseDobToIso(dateOfBirth || '');
  if (!iso) return null;
  const [year, month, day] = iso.split('-').map(Number);
  const hadBirthdayThisYear = today.getMonth() + 1 > month
    || (today.getMonth() + 1 === month && today.getDate() >= day);
  const age = today.getFullYear() - year - (hadBirthdayThisYear ? 0 : 1);
  return age >= 0 ? age : null;
};

export const resolveAgeRangeFromDob = (
  dateOfBirth: string,
  today: Date,
): AgeRangeResolution | null => {
  const age = calculateAgeOn(dateOfBirth, today);
  if (age === null) return null;
  if (age < YOUNG_QUESTIONNAIRE_MIN_AGE) return 'under-min';
  return age <= YOUNG_QUESTIONNAIRE_MAX_AGE ? 'young' : 'adult';
};

/** A draft counts as started once it holds at least one answer. */
const isStarted = (draft: QuestionnaireDraft | null): draft is QuestionnaireDraft => (
  !!draft && Object.keys(draft.answers || {}).length > 0
);

export interface InitialAgeRangeInput {
  adultDraft: QuestionnaireDraft | null;
  youngDraft: QuestionnaireDraft | null;
  dateOfBirth: string;
  today: Date;
}

/**
 * Pre-selection for the age range page: a started draft wins (the most recently
 * saved one when both exist), then the booking date of birth, then nothing.
 */
export const resolveInitialAgeRange = ({
  adultDraft,
  youngDraft,
  dateOfBirth,
  today,
}: InitialAgeRangeInput): AgeRangeResolution | null => {
  const adultStarted = isStarted(adultDraft);
  const youngStarted = isStarted(youngDraft);
  if (adultStarted && youngStarted) {
    return youngDraft.savedAt > adultDraft.savedAt ? 'young' : 'adult';
  }
  if (adultStarted) return 'adult';
  if (youngStarted) return 'young';
  return resolveAgeRangeFromDob(dateOfBirth, today);
};
